import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert } from '@/components/ui/Alert'
import { UserSearch } from '@/features/social/components/UserSearch'
import { FriendRequestList } from '@/features/social/components/FriendRequestList'
import { FriendList } from '@/features/social/components/FriendList'
import { ChallengeList } from '@/features/challenges/components/ChallengeList'
import { useFriends } from '@/features/social/hooks/useFriends'

const TABS = ['friends', 'challenges'] as const

type Tab = (typeof TABS)[number]

function FriendsSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      {['sk-1', 'sk-2', 'sk-3'].map((id) => (
        <div key={id} className="h-16 animate-pulse rounded-2xl bg-surface-container" />
      ))}
    </div>
  )
}

export function FriendsPage() {
  const { t } = useTranslation()
  const [tab, setTab] = useState<Tab>('friends')
  const { data, isLoading, isError } = useFriends()

  return (
    <div className="min-h-full overflow-y-auto bg-surface px-container-px-mobile pb-20 pt-6 md:px-container-px-desktop md:pb-8">
      <div className="mx-auto flex max-w-3xl flex-col gap-8">
        <h1 className="font-display text-2xl font-bold text-on-surface">
          {t('social.friends.page_title')}
        </h1>

        <div role="tablist" aria-label={t('social.friends.page_title')} className="flex gap-2">
          {TABS.map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              id={`social-tab-${value}`}
              aria-selected={tab === value}
              aria-controls="social-panel"
              onClick={() => setTab(value)}
              className={[
                'min-h-11 rounded-full px-4 text-sm font-semibold transition-colors',
                tab === value
                  ? 'bg-primary text-white'
                  : 'bg-surface-low text-on-surface-variant hover:bg-surface-container',
              ].join(' ')}
            >
              {t(`social.tabs.${value}`)}
            </button>
          ))}
        </div>

        <div
          role="tabpanel"
          id="social-panel"
          aria-labelledby={`social-tab-${tab}`}
          className="flex flex-col gap-8"
        >
          {tab === 'friends' ? (
            <>
              <UserSearch />

              {isError && <Alert variant="error">{t('social.friends.load_error')}</Alert>}
              {isLoading && <FriendsSkeleton />}

              {data && (
                <>
                  <FriendRequestList requests={data.pending} />
                  <FriendList friends={data.friends} />
                </>
              )}
            </>
          ) : (
            <ChallengeList />
          )}
        </div>
      </div>
    </div>
  )
}
